'use client';

import { IconMenu2, IconX } from '@tabler/icons-react';
import dynamic from 'next/dynamic';
import Link from 'next/link';

import { Badge } from '@j-s/components';

import { DEFAULT_NAV_LINKS, NavLinkItem } from '../constants/navigationLinks';
import { useClientTickets } from '../hooks/useClientTickets';

import { useHeaderScroll } from './useHeaderScroll';
import { useNavigationMenu } from './useNavigationMenu';

const TicketTrackerModal = dynamic(
  () =>
    import('../components/TicketTrackerModal').then(
      (mod) => mod.TicketTrackerModal,
    ),
  { ssr: false },
);

const MyTicketsDrawer = dynamic(
  () =>
    import('../components/MyTicketsDrawer').then((mod) => mod.MyTicketsDrawer),
  { ssr: false },
);

export type { NavLinkItem };

interface HeaderProps {
  logoSrc?: string;
  logoAlt?: string;
  links?: NavLinkItem[];
}

export function Header({
  logoSrc = '/img/napis.svg',
  logoAlt = 'Dom Rowerowy',
  links = DEFAULT_NAV_LINKS,
}: HeaderProps) {
  const { scrolled, activeSection, setActiveSection } = useHeaderScroll();
  const { pathname, menuOpen, setMenuOpen, handleNavClick } =
    useNavigationMenu(setActiveSection);
  const {
    savedTickets,
    activeTrackingId,
    activeTrackingToken,
    isDrawerOpen,
    setIsDrawerOpen,
    isModalOpen,
    openTicket,
    closeTicketModal,
    removeTicket,
  } = useClientTickets();

  const openTicketsCount = savedTickets.filter(
    (t) => t.lastStatus !== 'archived' && t.lastStatus !== 'closed',
  ).length;

  const hasNewServiceReply = savedTickets.some(
    (t) => t.lastStatus === 'replied',
  );

  return (
    <>
      <div
        id="menu"
        role="button"
        tabIndex={0}
        aria-label="Otwórz menu nawigacji"
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen((prev) => !prev)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setMenuOpen((prev) => !prev);
          }
        }}
      >
        <div className="flex items-center justify-center">
          <IconMenu2 size={20} />
        </div>
        <div>Menu</div>
        {(hasNewServiceReply || openTicketsCount > 0) && (
          <Badge
            content={hasNewServiceReply ? '!' : openTicketsCount}
            color={hasNewServiceReply ? 'success' : 'danger'}
            size="md"
          />
        )}
      </div>

      <div
        id="shadow"
        className={menuOpen ? 'open' : ''}
        aria-hidden={!menuOpen}
        onClick={() => setMenuOpen(false)}
      />

      <header className="image-bg relative w-full h-[340px] overflow-hidden flex items-center justify-center">
        <picture className="header-hero-picture absolute inset-0 w-full h-full pointer-events-none z-0">
          <source
            type="image/avif"
            srcSet="/img/ph12-h-sm.avif 640w, /img/ph12-h.avif 1200w"
            sizes="100vw"
          />
          <source
            type="image/webp"
            srcSet="/img/ph12-h-sm.webp 640w, /img/ph12-h.webp 1200w"
            sizes="100vw"
          />
          <img
            src="/img/ph12-h.webp"
            alt=""
            aria-hidden="true"
            fetchPriority="high"
            loading="eager"
            decoding="sync"
            className="header-hero-img w-full h-full object-cover object-center block"
          />
        </picture>
        <Link
          href="/"
          aria-label="Strona główna Dom Rowerowy"
          className="relative z-10 block w-[380px] max-w-[85%] mx-auto"
        >
          <img
            src={logoSrc}
            id="header-img"
            alt={logoAlt}
            width={537}
            height={326}
            fetchPriority="high"
            loading="eager"
            decoding="sync"
            className="w-full h-auto block"
          />
        </Link>
      </header>

      <div
        id="nav-replace"
        className={`${menuOpen ? 'nav-open' : ''} ${scrolled ? 'nav-scrolled' : ''}`}
      >
        <nav aria-label="Główna nawigacja">
          <div id="nav-inner">
            <div
              id="closeMenu"
              role="button"
              tabIndex={0}
              aria-label="Zamknij menu"
              onClick={() => setMenuOpen(false)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setMenuOpen(false);
                }
              }}
            >
              <IconX size={22} />
              <div>Zamknij</div>
            </div>

            {links.map(({ id, href, label, Icon }) => {
              const isActive = activeSection === id;
              const isContact = id === 'kontakt';
              const linkHref = pathname === '/' ? href : `/${href}`;

              const highlightContact =
                isContact && (hasNewServiceReply || openTicketsCount > 0);

              return (
                <a
                  key={id}
                  href={linkHref}
                  onClick={(e) => handleNavClick(e, href)}
                  className={`${isActive ? 'active-link' : ''} ${
                    highlightContact && !isActive
                      ? hasNewServiceReply
                        ? 'ring-2 ring-emerald-500/60 bg-emerald-500/10 text-emerald-950 shadow-xs'
                        : 'ring-1 ring-[#eded1e] bg-[#eded1e]/20 text-zinc-900'
                      : ''
                  }`}
                >
                  <Icon
                    size={19}
                    className={
                      isActive
                        ? 'text-[#18181b]'
                        : isContact && hasNewServiceReply
                          ? 'text-emerald-600'
                          : 'text-[#dc2626]'
                    }
                    aria-hidden="true"
                  />
                  <span className="link-label">{label}</span>
                  {isContact &&
                    (hasNewServiceReply || openTicketsCount > 0) && (
                      <Badge
                        content={hasNewServiceReply ? '!' : openTicketsCount}
                        color={hasNewServiceReply ? 'success' : 'danger'}
                        size="md"
                      />
                    )}
                </a>
              );
            })}
          </div>
        </nav>
      </div>

      <TicketTrackerModal
        ticketId={activeTrackingId}
        accessToken={activeTrackingToken}
        isOpen={isModalOpen}
        onClose={closeTicketModal}
      />

      <MyTicketsDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        tickets={savedTickets}
        onOpenTicket={openTicket}
        onRemoveTicket={removeTicket}
      />
    </>
  );
}
